import React, { useEffect, useState, useContext, useRef } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import Button from 'components/Shared/Buttons/Button';
import SearchDropdownMenu from 'components/Shared/SearchDropdownMenu';
import GetInTouch from 'components/Shared/GetInTouch';
import LogoRedLine from 'components/Shared/LogoRedLine';
import CartPreviewDropdown from 'components/RequestAQuote/CartPreviewDropdown';
import { motion, AnimatePresence } from 'framer-motion';
import { LocalContext } from 'context';
import { useWindowSize } from 'hooks';
import { useLocation } from '@reach/router';
import slugify from 'slugify';
import { Logo as SmallLogo, LongLogo, SearchIcon, CartIcon } from 'assets/svgs/';

function DesktopHeader({ stairCollections, doorCollections, userHasScrolled }) {
  const { cartCounter, counterRef, cartCounterDummyRef, quote } = useContext(LocalContext);
  const { pathname } = useLocation();
  const { windowWidth } = useWindowSize();
  const [underlinePosition, setUnderlinePosition] = useState(null);
  const [showDropdownList, setShowDropdownList] = useState(false);
  const [showSearchDropdownMenu, setShowSearchDropdownMenu] = useState(false);
  const [activeLink, setActiveLink] = useState(null);
  const [showCartPreview, setShowCartPreview] = useState(false);

  const listItemsRef = useRef();

  const handleLink = (e) => {
    if (e.type === 'click') {
      setActiveLink(e.target);
      setUnderlinePosition(e.target);
      setShowDropdownList(false);
    } else if (e.type === 'mouseenter') {
      setUnderlinePosition(e.target);
      if (!showDropdownList && e.target.innerText && e.target.innerText !== 'Trade') {
        if (showSearchDropdownMenu) {
          setShowSearchDropdownMenu(false);
        }
        setShowDropdownList(true);
      }
    } else if (e.type === 'mouseleave') {
      if (pathname === '/') {
        setActiveLink(null);
        setUnderlinePosition(null);
      } else if (e.target !== activeLink) {
        setUnderlinePosition(activeLink);
      }
    }
  };

  const handleList = (e) => {
    const target =
      e.target.tagName.toLowerCase() === 'ul'
        ? e.target.parentElement.previousSibling
        : e.target.parentElement.parentElement.parentElement.previousSibling;
    if (e.type === 'click') {
      setActiveLink(target);
      setUnderlinePosition(target);
      setShowDropdownList(false);
    } else if (e.type === 'mouseenter') {
      setUnderlinePosition(target);
    } else if (e.type === 'mouseleave') {
      if (pathname === '/') {
        setActiveLink(null);
        setUnderlinePosition(null);
      } else if (target !== activeLink) {
        setUnderlinePosition(activeLink);
      }
    }
  };

  useEffect(() => {
    if (pathname === '/') {
      setActiveLink(null);
      setUnderlinePosition(null);
    } else {
      const basePath = pathname.slice(0, pathname.indexOf('/', 2) + 1);
      const listItems = Array.from(listItemsRef.current.children).filter(
        (el) => el.nodeName.toLowerCase() === 'li'
      );
      const links = listItems.map((listItem) => listItem.children[0]);
      const _activeLink = links.find((link) => link.pathname === basePath);
      setActiveLink(_activeLink);
      setUnderlinePosition(_activeLink);
    }
  }, [pathname]);

  useEffect(() => {
    window.addEventListener('resize', () => {
      setUnderlinePosition(activeLink);
    });
  }, [activeLink]);

  const checkPathname = (_pathname) => {
    switch (true) {
      case _pathname === '/contact-us/':
        return false;
      case _pathname === '/request-a-quote/':
        return false;
      case /\/stairs\/.+/.test(_pathname):
        return false;
      default:
        return true;
    }
  };

  return (
    <Container $userHasScrolled={userHasScrolled}>
      <GetInTouch />
      <Link to="/" onClick={() => setUnderlinePosition(null)} aria-label="Home page">
        <StyledLongLogo />
        <LogoRedLine />
      </Link>
      <Nav
        $userHasScrolled={userHasScrolled}
        $showDropdownList={showDropdownList}
        $showSearchDropdownMenu={showSearchDropdownMenu}>
        <AnimatePresence>
          {userHasScrolled && (
            <SmallLogoWrapper>
              <Link to="/" onClick={() => setUnderlinePosition(null)} aria-label="Home page">
                <SmallLogo />
              </Link>
            </SmallLogoWrapper>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showSearchDropdownMenu && (
            <SearchDropdownMenu
              windowWidth={windowWidth}
              setShowSearchDropdownMenu={setShowSearchDropdownMenu}
            />
          )}
        </AnimatePresence>
        <ul
          ref={listItemsRef}
          onMouseLeave={() => {
            if (showDropdownList) {
              setShowDropdownList(false);
            }
          }}>
          <AnimatePresence>
            {underlinePosition && <Underline custom={underlinePosition} aria-hidden="true" />}
          </AnimatePresence>
          <li>
            <Link
              to="/stairs/"
              onClick={handleLink}
              onMouseEnter={handleLink}
              onMouseLeave={handleLink}
              aria-label="Staircases page">
              Staircases
            </Link>
            <div>
              <AnimatePresence>
                {showDropdownList && underlinePosition?.innerText === 'Staircases' && (
                  <DropdownList
                    custom={{
                      windowWidth,
                      heights: [254, 260]
                    }}
                    onMouseEnter={handleList}
                    onMouseLeave={handleList}>
                    {stairCollections.nodes
                      .sort((a, b) => {
                        const textA = a.frontmatter.title.toUpperCase();
                        const textB = b.frontmatter.title.toUpperCase();
                        return textA < textB ? -1 : textA > textB ? 1 : 0;
                      })
                      .map(({ frontmatter }, i) => {
                        const slug = slugify(frontmatter.slug, {
                          remove: /[*+~.,()'"!:@/]/g,
                          lower: true,
                          trim: true
                        });
                        return (
                          <DropdownListItem key={slug} custom={i}>
                            <Link to={`/stairs/${slug}`} onClick={handleList}>
                              {frontmatter.title.split(' ')[0]}
                            </Link>
                          </DropdownListItem>
                        );
                      })}
                    <br />
                    <DropdownListItem
                      style={{ fontWeight: 'bold' }}
                      custom={stairCollections.nodes.length}>
                      <Link to="/stairs/" onClick={handleList}>
                        View All Stairs
                      </Link>
                    </DropdownListItem>
                    <br />
                  </DropdownList>
                )}
              </AnimatePresence>
            </div>
          </li>
          <li>
            <Link
              to="/doors/"
              onClick={handleLink}
              onMouseEnter={handleLink}
              onMouseLeave={handleLink}
              aria-label="Doors page">
              Doors
            </Link>
            <div>
              <AnimatePresence>
                {showDropdownList && underlinePosition?.innerText === 'Doors' && (
                  <DropdownList
                    custom={{
                      windowWidth,
                      heights: [398, 404]
                    }}
                    onMouseEnter={handleList}
                    onMouseLeave={handleList}>
                    {doorCollections.nodes
                      .sort((a, b) => {
                        const textA = a.frontmatter.title.toUpperCase();
                        const textB = b.frontmatter.title.toUpperCase();
                        return textA < textB ? -1 : textA > textB ? 1 : 0;
                      })
                      .map(({ frontmatter }, i) => {
                        const slug = slugify(frontmatter.title, {
                          remove: /[*+~.,()'"!:@/]/g,
                          lower: true,
                          trim: true
                        });
                        return (
                          <DropdownListItem key={slug} custom={i}>
                            <Link to={`/doors/${slug}`} onClick={handleList}>
                              {frontmatter.title === 'Masonite Hollow Core'
                                ? 'Masonite HC'
                                : frontmatter.title}
                            </Link>
                          </DropdownListItem>
                        );
                      })}
                    <br />
                    <DropdownListItem
                      style={{ fontWeight: 'bold' }}
                      custom={doorCollections.nodes.length}>
                      <Link to="/doors/" onClick={handleList}>
                        View All Doors
                      </Link>
                    </DropdownListItem>
                    <br />
                  </DropdownList>
                )}
              </AnimatePresence>
            </div>
          </li>
          <li>
            <Link
              to="/stair-and-door-parts/"
              onClick={handleLink}
              onMouseEnter={handleLink}
              onMouseLeave={handleLink}
              aria-label="Stair & Door Parts page">
              Stair & Door Parts
            </Link>
            <div>
              <AnimatePresence>
                {showDropdownList && underlinePosition?.innerText === 'Stair & Door Parts' && (
                  <DropdownList
                    custom={{
                      windowWidth,
                      heights: [190, 224]
                    }}
                    onMouseEnter={handleList}
                    onMouseLeave={handleList}>
                    <DropdownListItem custom={0} style={{ marginBottom: '0.625em' }}>
                      <Link
                        to="/stair-and-door-parts/?collections=bannisters"
                        onClick={handleList}
                        style={{ lineHeight: '1.25em' }}
                        aria-label="Bannister Replacements page">
                        Bannister Replacements
                      </Link>
                    </DropdownListItem>
                    <DropdownListItem custom={1} style={{ marginBottom: '0.625em' }}>
                      <Link
                        to="/stair-and-door-parts/?collections=brackets,baserails,caps,clamps,posts,handrails,bases,tops,risers,spindles,slips,strings,treads"
                        onClick={handleList}
                        aria-label="Stairparts page">
                        Stairparts
                      </Link>
                    </DropdownListItem>
                    <DropdownListItem custom={2}>
                      <Link
                        to="/stair-and-door-parts/?collections=door frames,sliding doors,fire seals,architraves,skirtings,decorative,mouldings,corner blocks,oak,ogee"
                        onClick={handleList}
                        aria-label="Door Accessories page">
                        Door Accessories
                      </Link>
                    </DropdownListItem>
                    <br />
                    <DropdownListItem style={{ fontWeight: 'bold' }} custom={3}>
                      <Link
                        to="/stair-and-door-parts/"
                        onClick={handleList}
                        aria-label="Stair & Door Parts page">
                        View All Parts
                      </Link>
                    </DropdownListItem>
                    <br />
                  </DropdownList>
                )}
              </AnimatePresence>
            </div>
          </li>
          <li>
            <Link to="/showcases/" onMouseEnter={handleLink} onMouseLeave={handleLink}>
              Showcases
            </Link>
            <div />
          </li>
          <li>
            <Link
              to="/about-us/"
              onClick={handleLink}
              onMouseEnter={handleLink}
              onMouseLeave={handleLink}>
              About Us
            </Link>
            <div>
              <AnimatePresence>
                {showDropdownList && underlinePosition?.innerText === 'About Us' && (
                  <DropdownList
                    custom={{
                      windowWidth,
                      heights: [163, 142]
                    }}
                    onMouseEnter={handleList}
                    onMouseLeave={handleList}>
                    <DropdownListItem custom={0}>
                      <Link to="/about-us/" style={{ fontWeight: 'bold' }} onClick={handleList}>
                        About Us
                      </Link>
                    </DropdownListItem>
                    <DropdownListItem custom={1}>
                      <Link
                        to="/guides-and-manuals/"
                        style={{ fontWeight: 'bold', cursor: 'not-allowed' }}
                        // onClick={handleList}
                        onClick={(e) => e.preventDefault()}>
                        Guides & Manuals
                      </Link>
                    </DropdownListItem>
                    <DropdownListItem custom={2}>
                      <Link
                        to="/trade-partners/"
                        style={{ fontWeight: 'bold' }}
                        onClick={handleList}>
                        Trade Partners
                      </Link>
                    </DropdownListItem>
                    <br />
                  </DropdownList>
                )}
              </AnimatePresence>
            </div>
          </li>
          <li>
            <Link to="/trade-partners/" onMouseEnter={handleLink} onMouseLeave={handleLink}>
              Trade
            </Link>
            <div />
          </li>
        </ul>
        <IconsAndButton
          $showSearchDropdownMenu={showSearchDropdownMenu}
          onMouseLeave={() => {
            if (showCartPreview) {
              setShowCartPreview(false);
            }
          }}>
          <StyledSearchIcon
            onClick={() => setShowSearchDropdownMenu((currentValue) => !currentValue)}
          />
          <Cart to="/request-a-quote/" onMouseEnter={() => setShowCartPreview(true)}>
            <CartIcon />
            <CartCounterDummy ref={cartCounterDummyRef} />
            <CartCounter>
              <span ref={counterRef}>{cartCounter > 99 ? <small>99+</small> : cartCounter}</span>
            </CartCounter>
          </Cart>
          <Button
            to="/contact-us/"
            shine={checkPathname(pathname)}
            shineDuration={3}
            style={{
              marginLeft: '0.625rem',
              fontWeight: 700,
              whiteSpace: 'nowrap'
            }}>
            Contact Us
          </Button>
          <AnimatePresence>
            {showCartPreview && quote.length && pathname !== '/request-a-quote/' && (
              <CartPreviewDropdown
                quote={quote}
                cartCounter={cartCounter}
                setShowCartPreview={setShowCartPreview}
              />
            )}
          </AnimatePresence>
        </IconsAndButton>
      </Nav>
    </Container>
  );
}

const Container = styled.header`
  display: none;

  @media (width >= 1200px) {
    backdrop-filter: blur(0.9375rem);
    background-color: ${({ $userHasScrolled }) =>
      $userHasScrolled ? 'rgba(245, 245, 245, 0.5)' : 'rgba(245, 245, 245, 1)'};
    display: block;
    position: sticky;
    top: -110px;
    z-index: 3;
  }
`;

const StyledLongLogo = styled(LongLogo)`
  display: block;
  margin: 39px auto 0;
  max-width: 728px;
  width: 100%;
`;

const SmallLogoWrapper = styled(motion.div).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
})`
  align-self: end;
  grid-column: 1/3;
  width: 13.875rem;
  position: relative;
  top: 4px;
`;

const Nav = styled.nav`
  background-color: ${({ $userHasScrolled, $showDropdownList, $showSearchDropdownMenu }) =>
    $userHasScrolled && ($showDropdownList || $showSearchDropdownMenu) ? '#f5f5f5' : 'transparent'};
  column-gap: 1.25rem;
  display: grid;
  grid-template-columns: 100px 100px repeat(8, 1fr) 100px 100px;
  height: 4.375rem;
  overflow: visible;
  padding: 0 0.625rem;
  position: relative;

  > ul {
    display: flex;
    grid-column: 3 / 11;
    justify-content: center;

    > li {
      display: grid;
      flex-basis: 154px;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;
      height: 100%;

      > div {
        position: relative;
        grid-column: 1/2;
        grid-row: 1/2;
      }

      > a {
        align-items: center;
        align-self: center;
        background-color: ${({ $userHasScrolled }) =>
          $userHasScrolled ? 'transparent' : '#f5f5f5'};
        color: var(--connolly-black);
        display: flex;
        grid-column: 1/2;
        grid-row: 1/2;
        height: calc(100% - 7px);
        justify-content: center;
        position: relative;
        text-decoration: none;
        white-space: nowrap;
        z-index: 1;
      }
    }
  }
`;

const IconsAndButton = styled.div`
  align-items: center;
  bottom: -7px;
  display: flex;
  gap: 1.25rem;
  grid-column: 11/13;
  padding-bottom: 13px;
  padding-left: 100px;
  place-self: center end;
  position: relative;

  > svg * {
    stroke: ${({ $showSearchDropdownMenu }) =>
      $showSearchDropdownMenu ? 'var(--connolly-red)' : 'var(--connolly-black)'};
    transition: stroke 0.2s ease-in-out;
  }
`;

const StyledSearchIcon = styled(SearchIcon)`
  cursor: pointer;
  position: relative;
  top: 0.0625rem;
  width: 1.25rem;
`;

const Cart = styled(Link)`
  position: relative;
  width: 1rem;
`;

const CartCounterDummy = styled.div`
  aspect-ratio: 1;
  backface-visibility: hidden;
  border-radius: 100%;
  bottom: -0.5625rem;
  height: 1.25rem;
  position: absolute;
  right: -0.9375rem;
  width: 1.25rem;

  > * {
    position: relative;
  }
`;

const CartCounter = styled.strong`
  align-items: center;
  aspect-ratio: 1;
  background-color: var(--connolly-red);
  border-radius: 100%;
  bottom: -0.5625rem;
  display: flex;
  font-size: 0.75rem;
  justify-content: center;
  overflow: hidden;
  position: absolute;
  right: -0.9375rem;
  text-align: center;
  z-index: 1;

  span {
    color: #fff;
    width: 3ch;
  }
`;

const Underline = styled(motion.div).attrs({
  initial: (underlinePosition) => ({
    opacity: 0,
    x: underlinePosition?.offsetLeft,
    width: 0
  }),
  animate: (underlinePosition) => ({
    width: underlinePosition?.offsetWidth,
    x: underlinePosition?.offsetLeft,
    opacity: 1
  }),
  exit: () => ({
    opacity: 0
  }),
  transition: {
    duration: 0.5,
    ease: 'easeInOut'
  }
})`
  background-color: var(--connolly-red);
  bottom: 0;
  height: 0.219rem;
  left: 0;
  position: absolute;
`;

const DropdownList = styled(motion.ul).attrs({
  initial: {
    height: 0,
    opacity: 0
  },
  animate: ({ windowWidth, heights }) => ({
    height: windowWidth < 1265 ? heights[0] : heights[1],
    opacity: 1,
    transition: {
      type: 'tween',
      ease: 'easeInOut',
      duration: 0.5
    }
  }),
  exit: ({ windowWidth, heights }) => ({
    height: windowWidth < 1265 ? heights[0] : heights[1],
    opacity: 0,
    transition: {
      type: 'tween',
      ease: 'easeInOut',
      duration: 0.5
    }
  })
})`
  background-color: #e1e6e8;
  display: flex;
  flex-basis: 154px;
  flex-direction: column;
  font-size: 0.875rem;
  left: 0;
  padding: 24px 7px 0 9px;
  position: absolute;
  top: 100%;
  transition: background-color 0.2s ease-in-out;
  width: 100%;

  @media (width >= 1265px) {
    font-size: 1rem;
  }

  @media (width >= 1350px) {
    padding: 24px 14px 0 18px;
  }
`;

const DropdownListItem = styled(motion.li).attrs({
  initial: {
    opacity: 0
  },
  animate: (i) => ({
    opacity: 1,
    transition: {
      delay: 0.3 + 0.04 * i,
      ease: 'easeInOut'
    }
  })
})`
  line-height: 24px;

  a {
    color: var(--connolly-black);
    display: inline-block;
    height: 100%;
    text-decoration: none;
    width: 100%;

    &:hover {
      color: var(--connolly-red) !important;
      text-decoration: underline;
    }
  }
`;

export default DesktopHeader;
